import { Pagination, Tooltip } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowCircleRightIcon } from "../../assets/icons/arrow-circle-right.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/edit.svg";
import { ReactComponent as FolderPlusIcon } from "../../assets/icons/folder-plus.svg";
import { ReactComponent as KeyIcon } from "../../assets/icons/key.svg";
import { ReactComponent as MoreIcon } from "../../assets/icons/more.svg";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import { ReactComponent as User2Icon } from "../../assets/icons/user_2.svg";
import PageSizeSelector from '../../components/common/PageSizeSelector';
import ResponsiveTable, { ResponsiveTableColumn } from "../../components/common/ResponsiveTable";
import SearchWithTagsInput from '../../components/common/SearchWithTagsInput';
import {
    ControlsRow,
    PageSizeWrapper, PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../components/common/TableContainer.styled";
import CreateDataSourceDialog from "../../components/data-sources/CreateDataSourceDialog";
import DataSourcePermissionDialog from "../../components/data-sources/DataSourcePermissionDialog";
import { ActionButton, ContextMenu, ContextMenuItem, OptionsButton, OwnerInfo } from "../../components/data-sources/DataSourceTable.styled";
import GenericInputDialog from "../../components/dialogs/InputDialog";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { DataSource } from "../../models/models";
import { collectionService } from "../../services/collection.service";
import { parseError } from "../../services/http.service";
import { transformDataSources } from "../../utils/format-data-sources";
import { Header } from './AdminDataSourceList.styled';

const ADMIN_DATA_SOURCE_CURRENT_PAGE = 'adminDataSourceCurrentPage';
const ADMIN_DATA_SOURCE_PAGE_SIZE = 'adminDataSourcePageSize';
const ADMIN_DATA_SOURCE_SEARCH_TAGS = 'adminDataSourceSearchTags';

export interface EditDataSourcePermission {
    id: number;
    owner: string
}

const AdminDataSourceList: React.FC = () => {
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedEditDataSourcePermission, setSelectedEditDataSourcePermission] = useState<EditDataSourcePermission | null>(null)
    const [showPermissionDialog, setShowPermissionDialog] = useState(false);
    const [dataSourceToChangeName, setDataSourceToChangeName] = useState<DataSource | null>(null);
    const [isChangeNameDialogOpen, setIsChangeNameDialogOpen] = useState(false);
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const [searchTags, setSearchTags] = useState<string[]>(() => {
        try {
            const savedTags = sessionStorage.getItem(ADMIN_DATA_SOURCE_SEARCH_TAGS);
            return savedTags ? JSON.parse(savedTags) : [];
        } catch (e) {
            return [];
        }
    });
    const [currentPage, setCurrentPage] = useState(() => {
        try {
            const savedPage = sessionStorage.getItem(ADMIN_DATA_SOURCE_CURRENT_PAGE);
            return savedPage ? parseInt(savedPage, 10) : 1;
        } catch (e) {
            return 1;
        }
    });
    const [pageSize, setPageSize] = useState(() => {
        try {
            const savedPageSize = sessionStorage.getItem(ADMIN_DATA_SOURCE_PAGE_SIZE);
            return savedPageSize ? parseInt(savedPageSize, 10) : 10;
        } catch (e) {
            return 5;
        }
    });
    const [totalElements, setTotalElements] = useState(0);
    const pageSizeOptions = [5, 10, 20, 50];

    const fetchDataSources = useCallback(async () => {
        setLoading(true);
        try {
            if (searchTags.length > 0) {
                const params = new URLSearchParams();
                params.append('page', (currentPage - 1).toString());
                params.append('size', pageSize.toString());
                searchTags.forEach(tag => params.append('name', tag));

                const response = await axios.get('/documents/by-collection', {params});
                setDataSources(transformDataSources(response.data.content));
                setTotalElements(response.data.totalElements || 0);
            } else {
                collectionService.invalidateCache();
                const data = await collectionService.fetchCollections(currentPage - 1, pageSize);
                setDataSources(transformDataSources(data.content));
                setTotalElements(data.totalElements);
            }
        } catch (error) {
            setDataSources([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTags]);

    useEffect(() => {
        fetchDataSources();
    }, [fetchDataSources]);

    const handleTagsChange = (tags: string[]) => {
        sessionStorage.setItem(ADMIN_DATA_SOURCE_SEARCH_TAGS, JSON.stringify(tags));
        setSearchTags(tags);
        sessionStorage.setItem(ADMIN_DATA_SOURCE_CURRENT_PAGE, '1');
        setCurrentPage(1);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        sessionStorage.setItem(ADMIN_DATA_SOURCE_CURRENT_PAGE, value.toString());
        setCurrentPage(value);
    };

    const handlePageSizeChange = (size: number) => {
        sessionStorage.setItem(ADMIN_DATA_SOURCE_PAGE_SIZE, size.toString());
        setPageSize(size);
        sessionStorage.setItem(ADMIN_DATA_SOURCE_CURRENT_PAGE, '1');
        setCurrentPage(1);
    };

    const handleDelete = async (collectionId: number) => {
        try {
            await axios.delete(`/collections/${collectionId}/documents`);
            collectionService.invalidateCache();
            setDataSources(dataSources.filter(c => c.id !== collectionId));
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const openChangeNameDialog = (dataSourceId: number) => {
        const dataSourceToSet = dataSources.find(c => c.id === dataSourceId);
        if (dataSourceToSet) {
            setDataSourceToChangeName(dataSourceToSet);
            setIsChangeNameDialogOpen(true);
        }
    };

    const confirmChangeNameDataSource = async (dataSourceName: string) => {
        if (!dataSourceToChangeName) return;
        try {
            await axios.patch(`/collections/${dataSourceToChangeName.id}/name`, null, {
                params: {
                    collectionName: dataSourceName
                }
            });
            collectionService.invalidateCache();
            await fetchDataSources();
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            setIsChangeNameDialogOpen(false);
            setDataSourceToChangeName(null);
        }
    };

    const handleManagePermissions = (editDataSourcePermission: EditDataSourcePermission) => {
        setSelectedEditDataSourcePermission(editDataSourcePermission);
        setShowPermissionDialog(true);
    }

    const handleDataSourcePreview = useCallback((dataSourceId: number) => {
        navigate(`/data-sources/${dataSourceId}/documents`)
    }, [navigate])

    const handleDataSourceCreated = (dataSourceId: number) => {
        navigate(`/data-sources/${dataSourceId}/documents`);
    };

    const translateCollectionType = React.useCallback((type: string): string => {
        switch (type) {
            case 'MANUAL':
                return t('dataSourceTable.collectionType.manual');
            case 'AUTOMATIC':
                return t('dataSourceTable.collectionType.automatic');
            case 'DEFAULT':
                return t('dataSourceTable.collectionType.default');
            default:
                return type;
        }
    }, [t]);

    const [contextMenu, setContextMenu] = useState<{
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        dataSource: DataSource;
    } | null>(null);

    const handleContextMenu = useCallback((event: React.MouseEvent, dataSource: DataSource) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            anchorEl: event.currentTarget as HTMLElement,
            dataSource: dataSource,
        });
    }, []);

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handlePreviewClick = React.useCallback((dataSourceId: number, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }
        handleDataSourcePreview(dataSourceId);
    }, [handleDataSourcePreview]);

    const columns: ResponsiveTableColumn<DataSource>[] = useMemo(() => [
        {
            id: 'name',
            header: t('dataSourceTable.headers.name'),
            render: (ds) => (
                <button
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handlePreviewClick(ds.id, e)}
                    style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, margin: 0, color: 'inherit', textAlign: 'left' }}
                >
                    {ds.name}
                </button>
            ),
        },
        {
            id: 'lastModification',
            header: t('dataSourceTable.headers.lastModification'),
            render: (ds) => new Date(ds.lastModificationDate).toLocaleDateString(),
        },
        {
            id: 'documents',
            header: t('dataSourceTable.headers.documents'),
            render: (ds) => ds.amountOfDocuments,
        },
        {
            id: 'type',
            header: t('dataSourceTable.headers.type'),
            render: (ds) => translateCollectionType(String(ds.collectionType)),
        },
        {
            id: 'actions',
            header: '',
            headerLabel: t('common.actions') as string,
            cellClassName: 'actions-cell',
            render: (ds) => (
                <>
                    <Tooltip title={t('common.actions')}>
                        <ActionButton
                            className="mobile-only"
                            variant="outlined"
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.dataSource.id === ds.id ? `admin-ds-menu-${ds.id}` : undefined}
                            aria-expanded={contextMenu?.dataSource.id === ds.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, ds);
                            }}
                        >
                            {t('adminDataSourceList.actions')}
                        </ActionButton>
                    </Tooltip>
                    <Tooltip title={t('common.actions')}>
                        <OptionsButton
                            className="desktop-only"
                            style={{ width: 26 }}
                            size="small"
                            aria-label={t('common.actions') as string}
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.dataSource.id === ds.id ? `admin-ds-menu-${ds.id}` : undefined}
                            aria-expanded={contextMenu?.dataSource.id === ds.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, ds);
                            }}
                        >
                            <MoreIcon aria-hidden={true} focusable={false}/>
                        </OptionsButton>
                    </Tooltip>
                </>
            ),
        },
    ], [t, handlePreviewClick, translateCollectionType, handleContextMenu]);

    if (loading && dataSources.length === 0) return <div>Loading...</div>;

    return (
        <StyledTableContainer>
            <Header>
                <h2>{t('adminDataSourceList.title')}</h2>
                <ActionButton
                    variant="contained"
                    onClick={() => setIsCreateDialogOpen(true)}
                    startIcon={<FolderPlusIcon aria-hidden={true} focusable={false}/>}
                                        aria-label={t('adminDataSourceList.createButton') as string}
                                        sx={{backgroundColor: 'primary.main'}}
                >
                    {t('adminDataSourceList.createButton')}
                </ActionButton>
            </Header>

            <ControlsRow>
                <SearchWrapper>
                    <SearchWithTagsInput
                        onTagsChange={handleTagsChange}
                        initialTags={searchTags}
                        ariaLabel={t('adminDataSourceList.search.ariaLabel') as string}
                    />
                </SearchWrapper>
                <PageSizeWrapper>
                    <PageSizeSelector
                        options={pageSizeOptions}
                        selectedSize={pageSize}
                        onChange={handlePageSizeChange}
                        ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                        getSizeAriaLabel={(size) => t('pageSizeSelector.aria.setSize', { size }) as string}
                    />
                </PageSizeWrapper>
            </ControlsRow>

            <ResponsiveTable
                columns={columns}
                data={dataSources}
                getRowKey={(ds) => ds.id}
                emptyState={t('adminDataSourceList.emptyState')}
                ariaLabel={t('adminDataSourceList.table.ariaLabel') as string}
            />

            <ContextMenu
                id={contextMenu ? `admin-ds-menu-${contextMenu.dataSource.id}` : undefined}
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorEl={contextMenu?.anchorEl ?? null}
                anchorReference={contextMenu?.anchorEl ? 'anchorEl' : 'anchorPosition'}
                anchorPosition={
                    contextMenu && contextMenu.mouseY !== undefined && contextMenu.mouseX !== undefined
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
                MenuListProps={{
                    autoFocusItem: true,
                    'aria-labelledby': contextMenu ? `admin-ds-menu-${contextMenu.dataSource.id}` : undefined,
                }}
            >
                {contextMenu && [
                    contextMenu.dataSource.owner ? (
                        <ContextMenuItem key="owner">
                            <OwnerInfo>
                                <User2Icon aria-hidden={true} focusable={false}/>
                                {contextMenu.dataSource.owner.email}
                            </OwnerInfo>
                        </ContextMenuItem>
                    ) : null,
                    (
                        <ContextMenuItem key="details" onClick={() => {
                            handleCloseContextMenu();
                            handleDataSourcePreview(contextMenu.dataSource.id);
                        }}>
                            <ArrowCircleRightIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.details')}
                        </ContextMenuItem>
                    ),
                    (
                        <ContextMenuItem key="changeName" onClick={() => {
                            handleCloseContextMenu();
                            openChangeNameDialog(contextMenu.dataSource.id);
                        }}>
                            <EditIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.changeName')}
                        </ContextMenuItem>
                    ),
                    (
                        <ContextMenuItem key="permissions" onClick={() => {
                            handleCloseContextMenu();
                            handleManagePermissions({ id: contextMenu.dataSource.id, owner: contextMenu.dataSource.owner?.email || '' });
                        }}>
                            <KeyIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.permissions')}
                        </ContextMenuItem>
                    ),
                    (
                        <ContextMenuItem key="delete"
                            onClick={() => {
                                handleCloseContextMenu();
                                handleDelete(contextMenu.dataSource.id);
                            }}
                            style={{color: '#f44336'}}
                        >
                            <TrashIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.delete')}
                        </ContextMenuItem>
                    ),
                ]}
            </ContextMenu>

            {totalElements > 5 && (
                <PaginationWrapper>
                    <Pagination
                        count={Math.ceil(totalElements / pageSize)}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        aria-label={t('adminDataSourceList.pagination.ariaLabel') as string}
                    />
                </PaginationWrapper>
            )}

            <CreateDataSourceDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                onDataSourceCreated={handleDataSourceCreated}
            />

            {selectedEditDataSourcePermission && (
                <DataSourcePermissionDialog
                    open={showPermissionDialog}
                    dataSourceId={selectedEditDataSourcePermission.id}
                    owner={selectedEditDataSourcePermission?.owner}
                    onClose={() => setShowPermissionDialog(false)}
                />)}

            {dataSourceToChangeName && (
                <GenericInputDialog
                    open={isChangeNameDialogOpen}
                    title={t('inputDialog.title')}
                    label={t('inputDialog.label')}
                    required
                    initialValue={dataSourceToChangeName.name}
                    onClose={() => setIsChangeNameDialogOpen(false)}
                    onConfirm={(val) => confirmChangeNameDataSource(val)}
                />
            )}

        </StyledTableContainer>
    );
};

export default AdminDataSourceList;
